<template>
  <div class="media-manager">
    <div v-if="errorBanner" class="media-manager__banner media-manager__banner--error">
      {{ errorBanner }}
    </div>
    <div v-if="!writable && !loading" class="media-manager__banner media-manager__banner--warn">
      This folder is not writable by the server user. Uploads and new folders will fail until permissions are fixed.
    </div>
    <div v-if="successBanner" class="media-manager__banner media-manager__banner--ok">
      {{ successBanner }}
    </div>

    <div class="media-manager__toolbar">
      <div class="media-manager__toolbar-row">
        <div class="media-manager__toolbar-left">
          <label class="media-manager__label" for="media-root">Root</label>
          <select id="media-root" class="media-manager__select" v-model="root" @change="onRootChange">
            <option v-for="r in roots" :key="r" :value="r">/{{ r }}</option>
          </select>
        </div>
        <div class="media-manager__toolbar-right">
          <label class="media-manager__overwrite">
            <input type="checkbox" v-model="overwrite">
            Overwrite existing
          </label>
          <button type="button" class="button button-black" :disabled="loading || !writable" @click="promptFolder">
            New folder
          </button>
          <button type="button" class="button button-primary" :disabled="loading || !writable" @click="triggerUpload">
            Batch upload
          </button>
          <input
            ref="fileInput"
            type="file"
            :accept="fileAccept"
            multiple
            class="media-manager__file-input"
            @change="onFilesSelected"
          >
          <input
            ref="replaceInput"
            type="file"
            :accept="fileAccept"
            class="media-manager__file-input"
            @change="onReplaceSelected"
          >
        </div>
      </div>
      <div class="media-manager__toolbar-row media-manager__toolbar-row--filters">
        <div class="media-manager__field">
          <label class="media-manager__label" for="media-sort">Sort</label>
          <select id="media-sort" class="media-manager__select" v-model="sortKey">
            <option value="name-asc">Alphabetical (A–Z)</option>
            <option value="name-desc">Alphabetical (Z–A)</option>
            <option value="size-desc">Size (largest)</option>
            <option value="size-asc">Size (smallest)</option>
            <option value="date-desc">Date (newest)</option>
            <option value="date-asc">Date (oldest)</option>
          </select>
        </div>
        <div class="media-manager__field media-manager__field--grow">
          <label class="media-manager__label" for="media-filter">Filter</label>
          <input
            id="media-filter"
            type="search"
            class="media-manager__filter"
            v-model="nameFilter"
            placeholder="Filter by name…"
            autocomplete="off"
          >
        </div>
      </div>
    </div>

    <nav class="media-manager__breadcrumb" aria-label="Current path">
      <button type="button" class="media-manager__crumb" @click="goTo('')">/{{ root }}</button>
      <template v-for="(seg, idx) in pathSegments">
        <span :key="'sep-' + idx" class="media-manager__crumb-sep">/</span>
        <button
          :key="'seg-' + idx"
          type="button"
          class="media-manager__crumb"
          @click="goTo(pathSegments.slice(0, idx + 1).join('/'))"
        >{{ seg }}</button>
      </template>
    </nav>

    <div class="media-manager__body">
      <div class="media-manager__grid-wrap">
        <p v-if="loading" class="media-manager__status">Loading…</p>
        <p v-else-if="!items.length" class="media-manager__status">This folder is empty.</p>
        <p v-else-if="!sortedItems.length" class="media-manager__status">No items match “{{ nameFilter }}”.</p>
        <div v-else class="media-manager__grid">
          <button
            v-for="item in sortedItems"
            :key="item.type + ':' + item.path"
            type="button"
            class="media-manager__item"
            :class="{
              'media-manager__item--selected': selected && selected.path === item.path,
              'media-manager__item--dir': item.type === 'directory'
            }"
            @click="selectItem(item)"
            @dblclick="openItem(item)"
          >
            <div class="media-manager__thumb">
              <span v-if="item.type === 'directory'" class="media-manager__folder-icon" aria-hidden="true"></span>
              <video
                v-else-if="isVideoItem(item)"
                class="media-manager__thumb-video"
                :src="cacheBust(item.web_path)"
                muted
                preload="metadata"
              ></video>
              <img
                v-else
                :src="cacheBust(item.web_path)"
                :alt="item.name"
                loading="lazy"
                @error="onThumbError"
              >
            </div>
            <div class="media-manager__item-name" :title="item.name">{{ item.name }}</div>
          </button>
        </div>
      </div>

      <aside class="media-manager__detail" v-if="selected">
        <h2 class="media-manager__detail-title">{{ selected.name }}</h2>
        <div v-if="selected.type === 'file'" class="media-manager__preview">
          <video
            v-if="isVideoItem(selected)"
            :src="cacheBust(selected.web_path)"
            controls
            preload="metadata"
          ></video>
          <img
            v-else
            :src="cacheBust(selected.web_path)"
            :alt="selected.name"
          >
        </div>
        <dl class="media-manager__meta">
          <div class="media-manager__meta-row">
            <dt>Type</dt>
            <dd>{{ selected.type }}{{ selected.kind ? ' (' + selected.kind + ')' : '' }}</dd>
          </div>
          <div class="media-manager__meta-row">
            <dt>Path</dt>
            <dd><code>{{ selected.web_path }}</code></dd>
          </div>
          <div v-if="selected.type === 'file'" class="media-manager__meta-row">
            <dt>MIME</dt>
            <dd>{{ selected.mime || '—' }}</dd>
          </div>
          <div v-if="selected.type === 'file'" class="media-manager__meta-row">
            <dt>Size</dt>
            <dd>{{ formatBytes(selected.size) }}</dd>
          </div>
          <div v-if="selected.type === 'file' && selected.width" class="media-manager__meta-row">
            <dt>Dimensions</dt>
            <dd>{{ selected.width }} × {{ selected.height }}</dd>
          </div>
          <div class="media-manager__meta-row">
            <dt>Modified</dt>
            <dd>{{ formatDate(selected.modified_at) }}</dd>
          </div>
          <div class="media-manager__meta-row">
            <dt>Writable</dt>
            <dd>{{ selected.writable ? 'Yes' : 'No' }}</dd>
          </div>
        </dl>
        <div class="media-manager__detail-actions">
          <a
            v-if="selected.type === 'file'"
            class="button button-black"
            :href="selected.web_path"
            target="_blank"
            rel="noopener"
          >Open</a>
          <button
            v-if="selected.type === 'directory'"
            type="button"
            class="button button-primary"
            @click="openItem(selected)"
          >Open folder</button>
          <button
            v-if="selected.type === 'file'"
            type="button"
            class="button button-primary"
            :disabled="!writable"
            @click="triggerReplace"
          >Replace</button>
          <button
            type="button"
            class="button button-black"
            :disabled="loading || !selected.writable"
            @click="promptRename"
          >Rename</button>
          <button
            v-if="selected.type === 'file'"
            type="button"
            class="button media-manager__delete-btn"
            :disabled="loading || !selected.writable"
            @click="confirmDelete"
          >Delete</button>
        </div>
      </aside>
      <aside v-else class="media-manager__detail media-manager__detail--empty">
        <p>Select a file or folder to view details.</p>
      </aside>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MediaManager',
  props: {
    roots: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      root: (this.roots && this.roots[0]) || 'images',
      path: '',
      items: [],
      selected: null,
      writable: true,
      loading: false,
      overwrite: false,
      sortKey: 'name-asc',
      nameFilter: '',
      errorBanner: '',
      successBanner: '',
      replaceTargetName: null,
      thumbBust: Date.now(),
    };
  },
  computed: {
    pathSegments() {
      if (!this.path) return [];
      return this.path.split('/').filter(Boolean);
    },
    fileAccept() {
      if (this.root === 'storage/videos') {
        return 'video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.m4v';
      }
      return 'image/jpeg,image/png,image/gif,image/webp,image/svg+xml,.jpg,.jpeg,.png,.gif,.webp,.svg';
    },
    sortedItems() {
      const parts = (this.sortKey || 'name-asc').split('-');
      const field = parts[0];
      const dir = parts[1] === 'desc' ? -1 : 1;
      const filter = String(this.nameFilter || '').trim().toLowerCase();
      let items = (this.items || []).slice();

      if (filter) {
        items = items.filter((item) => String(item.name || '').toLowerCase().indexOf(filter) !== -1);
      }

      items.sort((a, b) => {
        // Folders always stay above files
        if (a.type !== b.type) {
          return a.type === 'directory' ? -1 : 1;
        }

        let cmp = 0;
        if (field === 'size') {
          const sa = a.type === 'file' ? (a.size || 0) : 0;
          const sb = b.type === 'file' ? (b.size || 0) : 0;
          cmp = sa - sb;
        } else if (field === 'date') {
          const da = a.modified_at || 0;
          const db = b.modified_at || 0;
          cmp = da - db;
        } else {
          cmp = String(a.name || '').localeCompare(String(b.name || ''), undefined, {
            sensitivity: 'base',
            numeric: true,
          });
        }

        if (cmp === 0) {
          return String(a.name || '').localeCompare(String(b.name || ''), undefined, {
            sensitivity: 'base',
            numeric: true,
          });
        }
        return cmp * dir;
      });

      return items;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    getToken() {
      const form = document.getElementById('media-manager-csrf');
      if (form) {
        const tokenEl = form.querySelector('input[name="_token"]');
        if (tokenEl) return tokenEl.value;
      }
      const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    },
    cacheBust(webPath) {
      const sep = webPath.indexOf('?') >= 0 ? '&' : '?';
      return webPath + sep + 'v=' + this.thumbBust;
    },
    formatBytes(bytes) {
      if (bytes == null) return '—';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    },
    formatDate(ts) {
      if (!ts) return '—';
      try {
        return new Date(ts * 1000).toLocaleString();
      } catch (e) {
        return '—';
      }
    },
    isVideoItem(item) {
      if (!item || item.type !== 'file') return false;
      if (item.kind === 'video') return true;
      const name = String(item.name || '').toLowerCase();
      return /\.(mp4|webm|mov|m4v)$/.test(name);
    },
    onThumbError(e) {
      e.target.style.opacity = '0.3';
    },
    onRootChange() {
      this.path = '';
      this.selected = null;
      this.nameFilter = '';
      this.load();
    },
    goTo(path) {
      this.path = path || '';
      this.selected = null;
      this.nameFilter = '';
      this.load();
    },
    selectItem(item) {
      this.selected = item;
      this.errorBanner = '';
      if (item.type === 'file') {
        this.fetchShow(item.path);
      }
    },
    openItem(item) {
      if (item.type === 'directory') {
        this.path = item.path;
        this.selected = null;
        this.nameFilter = '';
        this.load();
      }
    },
    clearBanners() {
      this.errorBanner = '';
      this.successBanner = '';
    },
    async load() {
      this.loading = true;
      this.clearBanners();
      try {
        const params = new URLSearchParams({
          root: this.root,
          path: this.path || '',
        });
        const res = await fetch('/dashboard/media/api/list?' + params.toString(), {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error((data && data.message) || 'Failed to load folder.');
        }
        this.items = data.items || [];
        this.writable = !!data.writable;
        this.path = data.path || '';
        if (this.selected) {
          const still = this.items.find((i) => i.path === this.selected.path);
          this.selected = still || null;
        }
      } catch (e) {
        this.errorBanner = e.message || 'Failed to load folder.';
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    async fetchShow(path) {
      try {
        const params = new URLSearchParams({
          root: this.root,
          path: path,
        });
        const res = await fetch('/dashboard/media/api/show?' + params.toString(), {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        const data = await res.json();
        if (res.ok && data.success && data.file) {
          this.selected = data.file;
        }
      } catch (e) {
        // keep list metadata
      }
    },
    promptFolder() {
      const name = window.prompt('New folder name:');
      if (!name) return;
      this.createFolder(name.trim());
    },
    async createFolder(name) {
      this.clearBanners();
      try {
        const body = new FormData();
        body.append('root', this.root);
        body.append('path', this.path || '');
        body.append('name', name);
        body.append('_token', this.getToken());

        const res = await fetch('/dashboard/media/api/folders', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-CSRF-TOKEN': this.getToken(),
          },
          credentials: 'same-origin',
          body,
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error((data && data.message) || 'Failed to create folder.');
        }
        this.successBanner = 'Folder created: ' + name;
        await this.load();
      } catch (e) {
        this.errorBanner = e.message || 'Failed to create folder.';
      }
    },
    triggerUpload() {
      this.replaceTargetName = null;
      this.$refs.fileInput.value = '';
      this.$refs.fileInput.click();
    },
    triggerReplace() {
      if (!this.selected || this.selected.type !== 'file') return;
      this.replaceTargetName = this.selected.name;
      this.$refs.replaceInput.value = '';
      this.$refs.replaceInput.click();
    },
    confirmDelete() {
      if (!this.selected || this.selected.type !== 'file') return;
      const name = this.selected.name;
      const path = this.selected.web_path;
      const ok = window.confirm(
        'Delete this file permanently?\n\n' + path + '\n\nThis cannot be undone.'
      );
      if (!ok) return;
      this.deleteFile(name, this.selected.path);
    },
    promptRename() {
      if (!this.selected) return;
      const current = this.selected.name;
      const next = window.prompt('Rename to:', current);
      if (next === null) return;
      const trimmed = String(next).trim();
      if (!trimmed || trimmed === current) return;
      this.renameItem(this.selected.path, trimmed);
    },
    async renameItem(path, newName) {
      this.clearBanners();
      this.loading = true;
      try {
        const body = new FormData();
        body.append('root', this.root);
        body.append('path', path || '');
        body.append('name', newName);
        body.append('_token', this.getToken());

        const res = await fetch('/dashboard/media/api/rename', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-CSRF-TOKEN': this.getToken(),
          },
          credentials: 'same-origin',
          body,
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error((data && data.message) || 'Failed to rename.');
        }
        this.successBanner = 'Renamed to: ' + newName;
        this.thumbBust = Date.now();
        await this.load();
        if (data.item) {
          this.selected = data.item;
        }
      } catch (e) {
        this.errorBanner = e.message || 'Failed to rename.';
      } finally {
        this.loading = false;
      }
    },
    async deleteFile(name, path) {
      this.clearBanners();
      this.loading = true;
      try {
        const body = new FormData();
        body.append('root', this.root);
        body.append('path', path || '');
        body.append('_token', this.getToken());

        const res = await fetch('/dashboard/media/api/delete', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-CSRF-TOKEN': this.getToken(),
          },
          credentials: 'same-origin',
          body,
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error((data && data.message) || 'Failed to delete file.');
        }
        this.selected = null;
        this.successBanner = 'Deleted: ' + name;
        this.thumbBust = Date.now();
        await this.load();
      } catch (e) {
        this.errorBanner = e.message || 'Failed to delete file.';
      } finally {
        this.loading = false;
      }
    },
    onFilesSelected(e) {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;
      this.uploadFiles(files, this.overwrite);
    },
    onReplaceSelected(e) {
      const files = Array.from(e.target.files || []);
      if (!files.length || !this.replaceTargetName) return;
      const asName = this.replaceTargetName;
      this.replaceTargetName = null;
      this.uploadFiles([files[0]], true, asName);
    },
    async uploadFiles(files, overwrite, asName) {
      this.clearBanners();
      this.loading = true;
      try {
        const body = new FormData();
        body.append('root', this.root);
        body.append('path', this.path || '');
        body.append('overwrite', overwrite ? '1' : '0');
        body.append('_token', this.getToken());
        if (asName) {
          body.append('as_name', asName);
        }
        files.forEach((f) => body.append('files[]', f));

        const res = await fetch('/dashboard/media/api/upload', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-CSRF-TOKEN': this.getToken(),
          },
          credentials: 'same-origin',
          body,
        });
        const data = await res.json();
        const uploadedCount = (data.uploaded && data.uploaded.length) || 0;
        const errorCount = (data.errors && data.errors.length) || 0;

        if (!res.ok && uploadedCount === 0) {
          const firstErr = data.errors && data.errors[0] && data.errors[0].message;
          throw new Error(firstErr || data.message || 'Upload failed.');
        }

        this.thumbBust = Date.now();
        if (uploadedCount) {
          this.successBanner = uploadedCount + ' file(s) uploaded'
            + (errorCount ? ('; ' + errorCount + ' failed') : '');
        }
        if (errorCount) {
          const msgs = data.errors.map((err) => err.message).join(' ');
          this.errorBanner = msgs;
        }
        await this.load();
      } catch (e) {
        this.errorBanner = e.message || 'Upload failed.';
      } finally {
        this.loading = false;
        if (this.$refs.fileInput) this.$refs.fileInput.value = '';
        if (this.$refs.replaceInput) this.$refs.replaceInput.value = '';
      }
    },
  },
};
</script>

<style scoped>
.media-manager {
  margin-top: 1rem;
}

.media-manager__banner {
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  border-radius: 4px;
  font-size: 0.95rem;
}

.media-manager__banner--error {
  background: #fde8e8;
  color: #9b1c1c;
}

.media-manager__banner--warn {
  background: #fef3c7;
  color: #92400e;
}

.media-manager__banner--ok {
  background: #def7ec;
  color: #03543f;
}

.media-manager__toolbar {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.media-manager__toolbar-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
}

.media-manager__toolbar-row--filters {
  justify-content: flex-start;
  gap: 1.25rem;
  padding-top: 0.25rem;
}

.media-manager__toolbar-left,
.media-manager__toolbar-right {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
}

.media-manager__field {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.media-manager__field--grow {
  flex: 1 1 16rem;
  min-width: 12rem;
}

.media-manager__field--grow .media-manager__filter {
  flex: 1 1 auto;
  width: 100%;
  max-width: 28rem;
}

.media-manager__label {
  font-weight: 600;
  margin-right: 0.25rem;
}

.media-manager__select {
  min-width: 12rem;
  padding: 0.35rem 0.5rem;
}

.media-manager__filter {
  min-width: 12rem;
  padding: 0.35rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 2px;
  font: inherit;
}

.media-manager__overwrite {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.9rem;
}

.media-manager__file-input {
  display: none;
}

.media-manager__breadcrumb {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 1rem;
  font-size: 0.95rem;
}

.media-manager__crumb {
  background: none;
  border: none;
  color: #1a56db;
  cursor: pointer;
  padding: 0;
  font: inherit;
}

.media-manager__crumb:hover {
  text-decoration: underline;
}

.media-manager__crumb-sep {
  color: #6b7280;
}

.media-manager__body {
  display: grid;
  grid-template-columns: 1fr minmax(240px, 320px);
  gap: 1.25rem;
  align-items: start;
}

@media (max-width: 900px) {
  .media-manager__body {
    grid-template-columns: 1fr;
  }
}

.media-manager__status {
  color: #6b7280;
  padding: 1rem 0;
}

.media-manager__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 0.75rem;
}

.media-manager__item {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 0.5rem;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
}

.media-manager__item:hover {
  border-color: #9ca3af;
}

.media-manager__item--selected {
  border-color: #1a56db;
  box-shadow: 0 0 0 1px #1a56db;
}

.media-manager__thumb {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  overflow: hidden;
  border-radius: 2px;
  margin-bottom: 0.4rem;
}

.media-manager__thumb img,
.media-manager__thumb-video {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.media-manager__thumb-video {
  background: #111;
}

.media-manager__item--dir {
  border-color: #f0c14b;
  background: #fffbeb;
}

.media-manager__item--dir:hover {
  border-color: #d97706;
  background: #fef3c7;
}

.media-manager__item--dir.media-manager__item--selected {
  border-color: #1a56db;
  background: #fffbeb;
}

.media-manager__item--dir .media-manager__thumb {
  background: linear-gradient(180deg, #fef9c3 0%, #fde68a 100%);
}

.media-manager__folder-icon {
  width: 56px;
  height: 42px;
  border-radius: 3px 3px 6px 6px;
  background: linear-gradient(180deg, #fcd34d 0%, #f59e0b 100%);
  box-shadow: 0 2px 0 #d97706, 0 3px 6px rgba(146, 64, 14, 0.25);
  position: relative;
}

.media-manager__folder-icon::before {
  content: '';
  position: absolute;
  top: -10px;
  left: 0;
  width: 24px;
  height: 12px;
  border-radius: 3px 6px 0 0;
  background: linear-gradient(180deg, #fbbf24 0%, #f59e0b 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.35);
}

.media-manager__item--dir .media-manager__item-name {
  font-weight: 700;
  color: #92400e;
}

.media-manager__item-name {
  font-size: 0.8rem;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.media-manager__detail {
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 1rem;
  background: #fff;
  position: sticky;
  top: 1rem;
}

.media-manager__detail--empty {
  color: #6b7280;
}

.media-manager__detail-title {
  font-size: 1.1rem;
  margin: 0 0 0.75rem;
  word-break: break-all;
}

.media-manager__preview {
  margin-bottom: 0.75rem;
  background: #f3f4f6;
  border-radius: 2px;
  overflow: hidden;
  max-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.media-manager__preview img,
.media-manager__preview video {
  max-width: 100%;
  max-height: 220px;
  object-fit: contain;
}

.media-manager__preview video {
  width: 100%;
  background: #111;
}

.media-manager__meta {
  margin: 0 0 1rem;
}

.media-manager__meta-row {
  display: grid;
  grid-template-columns: 6.5rem 1fr;
  gap: 0.5rem;
  padding: 0.35rem 0;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.85rem;
}

.media-manager__meta-row dt {
  font-weight: 600;
  color: #374151;
}

.media-manager__meta-row dd {
  margin: 0;
  word-break: break-all;
}

.media-manager__detail-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.media-manager__delete-btn {
  background: #9b1c1c;
  color: #fff;
  border-color: #9b1c1c;
}

.media-manager__delete-btn:hover:not(:disabled) {
  background: #771d1d;
  border-color: #771d1d;
  color: #fff;
}

.media-manager__delete-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/MediaManager.vue